import {
  RadioButtonGroup,
  RadioButton,
  Search,
  SkeletonText,
  Layer,
} from '@carbon/react';
import style from './single-select-form.module.scss';
import { useCallback, useMemo, useState, ReactNode, useEffect } from 'react';
import { debounce } from 'lodash-es';
import classNames from 'classnames';

// Base interface that all options must extend
export interface Option {
  // Unique identifier for the option
  id: string | number;
  // Display name of the well/curve
  name: string;
}

export interface SingleSelectFormProps<T extends Option> {
  // Array of type curve options to display.
  options: T[];
  // Currently selected option. Pass null or undefined for no selection.
  value?: T | null;
  // Callback function called when selection changes. Receives the selected option or null if "None" is selected.
  onChange: (value: T | null) => void;
  // Whether to show a "None" option at the top of the list
  showNoneOption?: boolean;
  // Function to render custom label for each option. If not provided, uses default rendering.
  renderLabel?: (option: T) => React.ReactNode;
  className?: string;
  formClassName?: string;
  onSearch?: (text: string, option: T) => boolean;
  header?: ReactNode;
  isLoading?: boolean;
  id: string;
}

export const SingleSelectForm = <T extends Option>({
  options,
  value,
  onChange,
  showNoneOption = false,
  renderLabel,
  className,
  formClassName,
  onSearch,
  header,
  isLoading = false,
  id,
}: SingleSelectFormProps<T>) => {
  const [filteredOptions, setFilteredOptions] = useState<T[]>(options);
  const [searchText, setSearchText] = useState<string>('');

  const [optimisticValue, setOptimisticValue] = useState<T | null | undefined>(
    value
  );

  useEffect(() => {
    setOptimisticValue(value);
  }, [value]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const selectedId =
    optimisticValue?.id || (showNoneOption ? 'none' : undefined);

  const handleChange = (valueId: string | number | undefined) => {
    if (valueId === 'none') {
      setOptimisticValue(null);
    } else {
      const selectedOption = options.find(
        (opt) => String(opt.id) === String(valueId)
      );
      if (selectedOption) {
        setOptimisticValue(selectedOption);
      }
    }

    setTimeout(() => {
      if (valueId === 'none') {
        onChange(null);
      } else {
        const selectedOption = options.find(
          (opt) => String(opt.id) === String(valueId)
        );
        if (selectedOption) {
          onChange(selectedOption);
        }
      }
    }, 0);
  };

  // Default label renderer for TypeCurveOption
  const defaultRenderLabel = useCallback((option: T) => {
    return option.name;
  }, []);

  const debouncedFilteredOptions = useMemo(() => {
    if (!onSearch) return;
    return debounce((text: string) => {
      if (!text) return setFilteredOptions(options);

      const filtered = options.filter((option) => onSearch(text, option));

      setFilteredOptions(filtered);
    }, 300);
  }, [options, onSearch]);

  return (
    <div className={classNames(style.container, className)}>
      {debouncedFilteredOptions && (
        <Layer>
          <Search
            value={searchText}
            labelText="Search"
            placeholder="Search input text"
            onChange={(e) => {
              const text = e.target.value;
              setSearchText(text);
              debouncedFilteredOptions(text);
            }}
          />
        </Layer>
      )}
      {header && <div className={style.header}>{header}</div>}
      {isLoading && (
        <div className={style.skeleton}>
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonText key={index} />
          ))}
        </div>
      )}
      {!isLoading && filteredOptions.length > 0 && (
        <div className={classNames(style.formContainer, formClassName)}>
          <RadioButtonGroup
            name={id}
            valueSelected={selectedId}
            onChange={handleChange}
            orientation="vertical"
          >
            {showNoneOption ? (
              // <div className={style.noneOptionContainer}>
              <RadioButton
                id="radio-none"
                labelText="None"
                value="none"
                className={style.radioButton}
              />
            ) : (
              // </div>
              <></>
            )}

            {filteredOptions.map((option) => (
              <RadioButton
                key={option.id}
                id={`radio-${option.id}`}
                value={option.id}
                className={style.radioButton}
                labelText={
                  renderLabel ? renderLabel(option) : defaultRenderLabel(option)
                }
              />
            ))}
          </RadioButtonGroup>
        </div>
      )}
    </div>
  );
};
